import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { CalendarIcon, PlusIcon, CheckIcon } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

export function DirectTimeEntryForm({ assignment, onComplete, onAddTime, onCancel, onLoadEntries, existingEntries = [] }) {
  const [netWorkMinutes, setNetWorkMinutes] = useState('');
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [entryDate, setEntryDate] = useState(new Date());
  const [actualQuantity, setActualQuantity] = useState('');
  const [workNotes, setWorkNotes] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [setupTimeMinutes, setSetupTimeMinutes] = useState('0');
  const [timeEntries, setTimeEntries] = useState(existingEntries);

  useEffect(() => {
    setTimeEntries(existingEntries);
  }, [existingEntries]);

  useEffect(() => {
    const h = parseFloat(hours) || 0;
    const m = parseFloat(minutes) || 0;
    const total = h * 60 + m;
    setNetWorkMinutes(total > 0 ? total.toString() : '');
  }, [hours, minutes]);

  useEffect(() => {
    const total = parseFloat(netWorkMinutes) || 0;
    if (total > 0) {
      const h = Math.floor(total / 60);
      const m = (total % 60);
      if (Math.abs(h - parseFloat(hours)) > 0.01 || Math.abs(m - parseFloat(minutes)) > 0.01) {
        setHours(h > 0 ? h.toString() : '');
        setMinutes(m > 0 ? m.toString() : '');
      }
    }
  }, [netWorkMinutes]);

  const resetForm = () => {
    setNetWorkMinutes('');
    setHours('');
    setMinutes('');
    setActualQuantity('');
    setWorkNotes('');
    setSetupTimeMinutes('0');
  };

  const handleAddTime = async (e) => {
    e.preventDefault();
    const totalMinutes = parseFloat(netWorkMinutes) || 0;
    if (totalMinutes < 0.1) {
      alert('Bitte geben Sie eine gültige Arbeitszeit ein (mindestens 0,1 Minuten).');
      return;
    }

    const directTimeData = {
      net_work_minutes: totalMinutes,
      entry_date: entryDate.toISOString().split('T')[0],
      actual_quantity: actualQuantity ? parseInt(actualQuantity) : null,
      work_notes: workNotes || null,
      completion_notes: null,
      setup_time_minutes: setupTimeMinutes ? parseInt(setupTimeMinutes) : null
    };
    
    await onAddTime(directTimeData);
    resetForm();
    if (onLoadEntries) {
      await onLoadEntries();
    }
  };

  const handleCompleteWithTime = async (e) => {
    e.preventDefault();
    const totalMinutes = parseFloat(netWorkMinutes) || 0;
    if (totalMinutes < 0.1) {
      alert('Bitte geben Sie eine gültige Arbeitszeit ein (mindestens 0,1 Minuten).');
      return;
    }

    const directTimeData = {
      net_work_minutes: totalMinutes,
      entry_date: entryDate.toISOString().split('T')[0],
      actual_quantity: actualQuantity ? parseInt(actualQuantity) : null,
      work_notes: workNotes || null,
      completion_notes: completionNotes || null,
      setup_time_minutes: setupTimeMinutes ? parseInt(setupTimeMinutes) : null
    };
    
    await onComplete(directTimeData);
  };

  const getTotalExistingMinutes = () => {
    return timeEntries.reduce((total, entry) => {
      if (entry.start_time && entry.end_time) {
        const start = new Date(entry.start_time);
        const end = new Date(entry.end_time);
        return total + Math.round((end - start) / (1000 * 60));
      }
      return total;
    }, 0);
  };

  const formatTime = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h}h ${m}min`;
    if (h > 0) return `${h}h`;
    return `${m}min`;
  };

  return (
    <div className="space-y-4">
      <div className="bg-green-50 p-3 rounded-lg border border-green-200">
        <h4 className="font-medium text-green-900 mb-2">Laser-Arbeitsschritt</h4>
        <p className="text-sm text-green-700">
          <strong>Aufgabe:</strong> {assignment.task?.title}
        </p>
        <p className="text-sm text-green-700">
          <strong>Arbeitsschritt:</strong> {assignment.work_step?.step_type}
        </p>
        {assignment.work_step?.notes && (
          <p className="text-sm text-green-700 mt-1">
            <strong>Hinweise:</strong> {assignment.work_step.notes}
          </p>
        )}
      </div>

      {timeEntries.length > 0 && (
        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2">
            Bereits erfasste Zeiten ({timeEntries.length} Einträge)
          </h4>
          <div className="text-sm text-blue-700 space-y-1">
            <p><strong>Gesamtzeit:</strong> {formatTime(getTotalExistingMinutes())}</p>
            {timeEntries.map((entry, index) => (
              <div key={entry.id || index} className="flex justify-between">
                <span>Eintrag {index + 1}:</span>
                <span>
                  {entry.start_time && entry.end_time ? 
                    formatTime(Math.round((new Date(entry.end_time) - new Date(entry.start_time)) / (1000 * 60))) :
                    'Unbekannt'
                  }
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <form className="space-y-4">
        <div>
          <Label htmlFor="entry_date">Arbeitsdatum</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="entry_date"
                variant="outline"
                className="w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {entryDate ? format(entryDate, "PPP", { locale: de }) : "Datum auswählen"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={entryDate}
                onSelect={setEntryDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-3">
          <Label>Nettoarbeitszeit eingeben</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="hours" className="text-sm">Stunden (z.B. 1,5)</Label>
              <Input
                id="hours"
                type="number"
                min="0"
                step="0.1"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="0"
              />
            </div>
            <div>
              <Label htmlFor="minutes" className="text-sm">Minuten (z.B. 30,5)</Label>
              <Input
                id="minutes"
                type="number"
                min="0"
                step="0.1"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                placeholder="0"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="net_work_minutes" className="text-sm">Oder: Gesamtminuten direkt (z.B. 120,5)</Label>
            <Input
              id="net_work_minutes"
              type="number"
              min="0.1"
              step="0.1"
              value={netWorkMinutes}
              onChange={(e) => setNetWorkMinutes(e.target.value)}
              placeholder="z.B. 120.5 für 2 Stunden 30,5 Minuten"
            />
          </div>

          {netWorkMinutes && parseFloat(netWorkMinutes) > 0 && (
            <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
              Eingegebene Zeit: <strong>{formatTime(parseFloat(netWorkMinutes))}</strong> ({netWorkMinutes} Minuten)
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="actual_quantity">Ist-Stückzahl (optional)</Label>
          <Input
            id="actual_quantity"
            type="number"
            min="0"
            value={actualQuantity}
            onChange={(e) => setActualQuantity(e.target.value)}
            placeholder="Anzahl bearbeiteter Stücke"
          />
        </div>

        <div>
          <Label htmlFor="setup_time_minutes">🔧 Rüstzeit (in 10-Minuten-Schritten)</Label>
          <select
            id="setup_time_minutes"
            value={setupTimeMinutes}
            onChange={(e) => setSetupTimeMinutes(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
          >
            <option value="0">Keine Rüstzeit (0 min)</option>
            <option value="10">10 Minuten</option>
            <option value="20">20 Minuten</option>
            <option value="30">30 Minuten</option>
            <option value="40">40 Minuten</option>
            <option value="50">50 Minuten</option>
            <option value="60">1 Stunde (60 min)</option>
            <option value="70">1 Stunde 10 Minuten</option>
            <option value="80">1 Stunde 20 Minuten</option>
            <option value="90">1 Stunde 30 Minuten</option>
            <option value="100">1 Stunde 40 Minuten</option>
            <option value="110">1 Stunde 50 Minuten</option>
            <option value="120">2 Stunden (120 min)</option>
            <option value="130">2 Stunden 10 Minuten</option>
            <option value="140">2 Stunden 20 Minuten</option>
            <option value="150">2 Stunden 30 Minuten</option>
            <option value="160">2 Stunden 40 Minuten</option>
            <option value="170">2 Stunden 50 Minuten</option>
            <option value="180">3 Stunden (180 min)</option>
          </select>
          {setupTimeMinutes !== '0' && (
            <div className="text-sm text-green-700 bg-green-50 p-2 rounded mt-2">
              <strong>Ausgewählte Rüstzeit:</strong> {setupTimeMinutes} Minuten ({Math.floor(parseInt(setupTimeMinutes) / 60)}h {parseInt(setupTimeMinutes) % 60}min)
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="work_notes">Arbeitsnotizen (optional)</Label>
          <Textarea
            id="work_notes"
            value={workNotes}
            onChange={(e) => setWorkNotes(e.target.value)}
            placeholder="Beschreibung der durchgeführten Arbeiten..."
            rows={2}
          />
        </div>

        <div>
          <Label htmlFor="completion_notes">Abschluss-Notizen (nur beim Abschließen)</Label>
          <Textarea
            id="completion_notes"
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            placeholder="Finale Notizen zum abgeschlossenen Arbeitsschritt..."
            rows={2}
          />
        </div>

        <div className="flex flex-col space-y-2">
          <Button 
            type="button"
            onClick={handleAddTime}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={!netWorkMinutes || parseFloat(netWorkMinutes) < 0.1}
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Zeiteintrag hinzufügen
          </Button>
          
          <Button 
            type="button"
            onClick={handleCompleteWithTime}
            className="bg-green-600 hover:bg-green-700"
            disabled={!netWorkMinutes || parseFloat(netWorkMinutes) < 0.1}
          >
            <CheckIcon className="h-4 w-4 mr-2" />
            Eintrag hinzufügen und Arbeitsschritt abschließen
          </Button>
          
          <Button type="button" variant="outline" onClick={onCancel}>
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  );
}

export default DirectTimeEntryForm;
